import { z } from 'zod';

/**
 * Predictable error codes. The HTTP status is part of the contract
 * so clients can branch on either. `message` is a developer-facing English
 * sentence; UIs map `code` to their own (Korean) copy.
 */
export const ERROR_STATUS = {
  UNAUTHENTICATED: 401,
  TOKEN_EXPIRED: 401,
  SESSION_REVOKED: 401,
  INVALID_CREDENTIALS: 401,
  MFA_INVALID: 401,
  CSRF_FAILED: 403,
  FORBIDDEN: 403,
  ACCOUNT_DEACTIVATED: 403,
  READ_ONLY_CONVERSATION: 403,
  EDIT_WINDOW_EXPIRED: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  REVISION_CONFLICT: 409,
  IDEMPOTENCY_MISMATCH: 409,
  ALREADY_MEMBER: 409,
  EMAIL_TAKEN: 409,
  LAST_OWNER: 409,
  INVITE_ALREADY_ACCEPTED: 409,
  FILE_NOT_READY: 409,
  FILE_UNSCANNED_ACK_REQUIRED: 409,
  INVITE_INVALID: 410,
  INVITE_EXPIRED: 410,
  INVITE_REVOKED: 410,
  FILE_INFECTED: 410,
  FILE_TOO_LARGE: 413,
  UPLOAD_SIZE_MISMATCH: 400,
  RANGE_NOT_SATISFIABLE: 416,
  VALIDATION_FAILED: 422,
  FILE_TYPE_NOT_ALLOWED: 422,
  TOO_MANY_FILES: 422,
  TOO_MANY_REACTIONS: 422,
  UNSUPPORTED_CLIENT: 426,
  RATE_LIMITED: 429,
  TOO_MANY_UPLOADS: 429,
  INTERNAL: 500,
  STORAGE_FULL: 507,
  MAINTENANCE: 503,
  SETUP_REQUIRED: 503,
} as const satisfies Record<string, number>;

export type ErrorCode = keyof typeof ERROR_STATUS;
export const ErrorCode = z.enum(Object.keys(ERROR_STATUS) as [ErrorCode, ...ErrorCode[]]);

export function isErrorCode(value: string): value is ErrorCode {
  return Object.hasOwn(ERROR_STATUS, value);
}

/** Code for an error this app does not know (sent by a newer server), chosen by HTTP status. */
export function errorCodeForStatus(status: number): ErrorCode {
  if (status === 401) return 'UNAUTHENTICATED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 426) return 'UNSUPPORTED_CLIENT';
  if (status === 429) return 'RATE_LIMITED';
  if (status === 503) return 'MAINTENANCE';
  if (status === 507) return 'STORAGE_FULL';
  // Any other 4xx is a refusal, never retried.
  if (status >= 400 && status < 500) return 'VALIDATION_FAILED';
  return 'INTERNAL';
}

export const ApiErrorBody = z.object({
  error: z.object({
    /** Read as a string so codes added by a newer server do not break parsing (see `errorCodeForStatus`). */
    code: z.string(),
    message: z.string(),
    /** Structured, secret-free details (e.g. `{ current: Message }` for REVISION_CONFLICT). */
    details: z.unknown().optional(),
    requestId: z.string().optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBody>;

/** Error raised by both the server (to serialize) and the client (after parsing a response). */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(code: ErrorCode, message?: string, details?: unknown, requestId?: string) {
    super(message ?? code);
    this.name = 'ApiError';
    this.code = code;
    this.status = ERROR_STATUS[code];
    this.details = details;
    this.requestId = requestId;
  }

  toBody(): ApiErrorBody {
    return {
      error: {
        code: this.code,
        message: this.message,
        ...(this.details === undefined ? {} : { details: this.details }),
        ...(this.requestId ? { requestId: this.requestId } : {}),
      },
    };
  }
}

export function isApiError(value: unknown, code?: ErrorCode): value is ApiError {
  return value instanceof ApiError && (code === undefined || value.code === code);
}
